import { useEffect, useState } from "react";
import { PRICE } from "./content.ts";

/** Counts up to a value once, unless the visitor prefers less motion. */
function useCountUp(target: number, ms = 900) {
  const [v, setV] = useState(target);
  useEffect(() => {
    // Only when someone can see it, so a page opened in the background still shows the real number.
    if (
      document.visibilityState !== "visible" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      setV(target * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setV(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

const usd = (n: number, cents = false) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });

/**
 * A real outage, measured the way recoup measures it: Snowflake's AWS Europe (Ireland) region,
 * 21 October 2026 (INC20000301 on status.snowflake.com, major outage 18:26–23:29 UTC), under
 * Snowflake's published SLA: below 99.9% for the month pays one day's average usage, claimed
 * within 21 days of the month's end. The fee is an example; the credit scales with it.
 */
export const REAL_EXAMPLE = {
  vendor: "Snowflake",
  incident: "INC20000301",
  fee: 31_000,
  measured: "99.32%",
  /** One day's average usage: the month's fee over October's 31 days. */
  credit: 1_000,
  claimBy: "Nov 21",
} as const;

/** The money, shown the way it arrives: a credit memo against the vendor's next invoice. */
export function CreditMemo() {
  const x = REAL_EXAMPLE;
  const credit = useCountUp(x.credit);
  const rows: [string, string][] = [
    [`Fee, ${x.vendor}`, `${usd(x.fee)} / mo`],
    ["Promised uptime", "99.9%"],
    ["Measured in October", x.measured],
    ["Below 99.9% pays back", "1 day's usage"],
  ];
  return (
    <div className="rise relative mx-auto w-full max-w-md">
      <div className="memo rounded-b-card px-6 pt-8 pb-6 shadow-hero">
        <div className="flex items-baseline justify-between gap-3 eyebrow">
          <span>Credit memo · Oct 2026</span>
        </div>
        <dl className="mt-5 flex flex-col gap-2.5 font-mono text-sm">
          {rows.map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-4 border-b border-dashed border-line pb-2.5"
            >
              <dt className="text-muted">{k}</dt>
              <dd className="tabular text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-5 flex items-end justify-between gap-4">
          <span className="text-sm font-medium text-muted">Owed to you</span>
          <span className="tabular font-display text-5xl font-bold tracking-tight text-ink">
            <span className="marker">{usd(credit, true)}</span>
          </span>
        </div>
        <p className="mt-4 font-mono text-label text-faint">
          {x.incident} · claim by {x.claimBy} · proof attached
        </p>
      </div>
      <span className="stamp stamp-in absolute top-6 right-5 bg-memo/80 px-2 py-1 font-mono text-xs font-semibold text-accent">
        OWED
      </span>
    </div>
  );
}

/** A real SLA's three parts, marked the way a person reads one with a highlighter. */
export function ClauseCard() {
  return (
    <figure className="rounded-card border border-line bg-surface p-6 shadow-card">
      <figcaption className="eyebrow">How a typical contract reads</figcaption>
      <blockquote className="mt-4 font-serif text-prose leading-relaxed text-ink">
        The service will be available with a monthly uptime percentage of{" "}
        <mark className="mark-clause">at least 99.9%</mark>. If it falls short, customer is eligible
        for <mark className="mark-clause">a service credit of 10% of the monthly fee</mark>. To
        receive a credit, customer must submit a request{" "}
        <mark className="mark-clause">within 15 days after the end of the month</mark>.
      </blockquote>
      <ol className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
        {[
          ["The promise", "What the vendor promised"],
          ["The money back", "What a miss is worth"],
          ["The deadline", "After this, it's gone"],
        ].map(([t, b], i) => (
          <li key={t} className="flex gap-2.5">
            <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-full bg-hl font-mono text-xs font-semibold text-hl-ink">
              {i + 1}
            </span>
            <span>
              <span className="block font-semibold">{t}</span>
              <span className="text-muted">{b}</span>
            </span>
          </li>
        ))}
      </ol>
    </figure>
  );
}

/** The whole price, three lines: what's free, what's monthly, and what's shared. */
export function PriceBreakdown() {
  const rows: [string, string, string][] = [
    ["Free audit", "$0", `Up to ${PRICE.auditContracts} contracts and the last 12 months`],
    [
      "Monitoring",
      `$${PRICE.perVendor} per vendor a month`,
      `At least ${PRICE.minVendors} vendors, so from $${PRICE.perVendor * PRICE.minVendors} a month`,
    ],
    ["Recovery", `${PRICE.successFee}% of what comes back`, "Only once the credit is on your bill"],
  ];
  return (
    <dl className="flex flex-col divide-y divide-white/15 rounded-panel bg-white/5 px-6 text-band-ink">
      {rows.map(([k, v, note]) => (
        <div key={k} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-5">
          <dt className="text-sm text-band-muted">{k}</dt>
          <dd className="text-right">
            <span className="font-display text-2xl font-bold tracking-tight">{v}</span>
            <span className="block text-sm text-band-muted">{note}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}
